import React, { useState, lazy, Suspense } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@linaria/core'
import { useOverlay, Separator, Button } from '@fs/zion-ui'
import { useQueryParams } from '@fs/zion-router'
import MemoryTile from '@fs/zion-memory-tile'
import { trackEvent } from '@fs/zion-analytics'
import InfoCard from '../info-card/PersonInfoCard'
import CountHeader from '../count-header/CountHeader'
import NoMemories from './NoMemories'
import MemoryCallToAction from './MemoryCallToAction'

const MediaOverlay = lazy(() => import('../media-overlay/MediaOverlay'))

const memoryContainerCSS = css`
  display: grid;
  position: relative;
  width: 100%;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 8px;
`

function getThumbnailUrl(memory) {
  let thumbnailUrl = memory.thumbnailUrl
  if (!thumbnailUrl) {
    thumbnailUrl = memory?.associatedMedia?.find((artifact) => {
      return artifact.thumbnailUrl
    })
  }
  return thumbnailUrl
}

function getMemoryTileType(memory) {
  let tileType = memory.memoryType
  if (memory.memoryType === 'PHOTO') {
    tileType = memory?.associatedMedia?.find((artifact) => {
      return artifact.memoryType === 'AUDIO'
    })
      ? 'AUDIO'
      : 'PHOTO'
  }
  return tileType ? tileType.toLowerCase() : 'PHOTO'
}

export default function MemoriesCard({
  memories = [],
  memoryCount,
  person,
  baseUrl,
  loggedIn = false,
  restricted = false,
  ...props
}) {
  const [t] = useTranslation()
  const displayedMemories = restricted ? memories.slice(0, 6) : memories.slice(0, 5)
  const viewAllMemory = memories[5]
  const noMemories = memoryCount <= 0
  const { query } = useQueryParams()
  const [selectedMemory, setSelectedMemory] = useState(findMemory() ?? memories[0])
  const mediaOverlay = useOverlay({ initiallyOpen: !!findMemory() })

  function findMemory() {
    return query?.memoryUrl ? memories.find((memory) => query?.memoryUrl?.includes(memory.url)) : undefined
  }

  function onMemoryClick(index, event) {
    trackEvent({ link_name: 'Memories: Memory' })
    if (!loggedIn) {
      setSelectedMemory(memories[index])
      mediaOverlay.handleClick(event)
    }
  }

  return (
    <InfoCard heading={<CountHeader title={t('info-cards.memories.header')} count={memoryCount} />} {...props}>
      {!noMemories && (
        <div className={memoryContainerCSS}>
          {displayedMemories.map((memory, index) => {
            const key = `memory-item-${index}`
            return (
              <MemoryTile
                key={key}
                type={getMemoryTileType(memory)}
                image={getThumbnailUrl(memory)}
                onClick={(event) => onMemoryClick(index, event)}
                data-testid="memory-tile"
                to={loggedIn ? memory.memoryUrl : undefined}
                external={loggedIn}
                target={loggedIn ? '_blank' : undefined}
              />
            )
          })}
          {!restricted && viewAllMemory && (
            <MemoryTile
              type={getMemoryTileType(viewAllMemory)}
              image={getThumbnailUrl(viewAllMemory)}
              viewAllCount={memoryCount - 5}
              to={`${baseUrl || ''}/tree/person/memories/${person.id}`}
              linkName="Memories: View All"
              external
              data-testid="memory-tile-view-all"
            />
          )}
        </div>
      )}

      {(mediaOverlay.isOpen || mediaOverlay.transitioning) && (
        <Suspense fallback="">
          <MediaOverlay
            {...mediaOverlay}
            title={selectedMemory?.memoryType !== 'STORY' && selectedMemory?.headline}
            media={memories}
            mediaType="memory"
            mediaCount={memoryCount}
            selectedMedia={selectedMemory}
            setSelectedMedia={setSelectedMemory}
            restricted={restricted}
            person={person}
            loggedIn={loggedIn}
            baseUrl={baseUrl}
          />
        </Suspense>
      )}

      {noMemories && <NoMemories />}
      <Separator size="xxs" />
      {!restricted && <MemoryCallToAction person={person} loggedIn={loggedIn} baseUrl={baseUrl} />}
      {restricted && !noMemories && (
        <Button
          data-testid="memories-view-all-restricted"
          linkName="Memories: View All Restricted"
          onClick={() => {
            mediaOverlay.handleClick()
          }}
        >
          {t('info-cards.view-all.action')}
        </Button>
      )}
    </InfoCard>
  )
}

// Stryker disable all
MemoriesCard.propTypes = {
  /** Memories to display tiles for. */
  memories: PropTypes.arrayOf(PropTypes.shape({})),

  /** Total number of memories (this is not memories.length - it's the total number of memories the person has). */
  memoryCount: PropTypes.number.isRequired,

  /** Person object for the person who's memories are being displayed. */
  person: PropTypes.shape({}),
}
